import { useState, useEffect, useRef } from "react";
import { Loader2, FileText, Printer, Repeat, ScanLine, Search, Check, Sparkles, Send, Info, ExternalLink, Calculator, X, Bell, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";
import { classifyVehicleType } from "../../../lib/classifyVehicleType";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import ZB1ScanCard from "../ZB1ScanCard";
import MobileZb1QrCard from "../../workshop-orders/MobileZb1QrCard";
import BrandCombobox from "../BrandCombobox";
import MonthYearPicker from "../MonthYearPicker";
import { MOTO_BRANDS, MOTORCYCLE_VEHICLE_TYPES, CONDITION_OPTIONS } from "../../../lib/listingConstants";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";

// ---------- Phase 5 — Inzahlungnahme: Eintausch-Fahrzeug + Ankaufsvertrag -----

// Jun-2026 (User-Wunsch) — Jahr aus ZB1-Erstzulassungsdatum (TT.MM.JJJJ / JJJJ-..) ableiten.
const yearFromDate = (s) => {
    if (!s) return "";
    const m = String(s).match(/(19|20)\d{2}/);
    return m ? m[0] : "";
};

// Jun-2026 (User-Wunsch · Msg 509) — Merker für „von Genauere Daten (KBA) zurückgekehrt".
const KBA_FLAG = (id) => `tw_tradein_kba_visited_${id}`;

export function TradeInModal({ listingId, existing, defaultPrice = 0, initialMode = "manual", checklist = null, customerWishPrice = null, onBack, onClose, onSaved, onGoToPayment }) {
    const [veh, setVeh] = useState({
        make: existing?.vehicle?.make || "", model: existing?.vehicle?.model || "",
        category: existing?.vehicle?.category || "motorcycle",
        vehicle_type: existing?.vehicle?.vehicle_type || "",
        first_registration_date: existing?.vehicle?.first_registration_date || "",
        year: existing?.vehicle?.year || "", mileage: existing?.vehicle?.mileage ?? "",
        displacement_ccm: existing?.vehicle?.displacement_ccm ?? existing?.vehicle?.displacement ?? "",
        power_kw: existing?.vehicle?.power_kw ?? "",
        fuel: existing?.vehicle?.fuel || "",
        emission_class: existing?.vehicle?.emission_class || "",
        hsn: existing?.vehicle?.hsn || "",
        tsn: existing?.vehicle?.tsn || "",
        vehicle_class: existing?.vehicle?.vehicle_class || "",
        vin: existing?.vehicle?.vin || "",
        condition: CONDITION_OPTIONS.includes(existing?.vehicle?.condition) ? existing.vehicle.condition : "Gebrauchtfahrzeug",
        tuev_until: existing?.vehicle?.tuev_until || "",
        tuev_none: existing?.vehicle?.tuev_none || false,
        tuev_none_reason: existing?.vehicle?.tuev_none_reason || "",
        notes: existing?.vehicle?.notes || "",
    });
    const [price, setPrice] = useState(existing?.price ?? defaultPrice ?? 0);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(existing || null);
    // KI-Preisermittlung (Ankaufspreis-Schätzung)
    const [aiLoading, setAiLoading] = useState(false);
    const [datLoading, setDatLoading] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    // Genehmigung zum Ankauf (Freigabe durch Filialleiter/Verkaufsleiter)
    const [approval, setApproval] = useState(existing?.approval || null);
    const [reqApproval, setReqApproval] = useState(false);
    // scan | search | null
    const [panel, setPanel] = useState(initialMode === "scan" ? "scan" : initialMode === "search" ? "search" : null);
    const [searchQ, setSearchQ] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [linkedId, setLinkedId] = useState(null);
    const upd = (k, v) => setVeh((p) => ({ ...p, [k]: v }));
    // Alle Pflichtfelder für Speichern & Preisermittlung an EINER Stelle prüfen
    // (inkl. Laufleistung & TÜV — TÜV ist erfüllt bei Monat/Jahr ODER „kein TÜV"
    // mit gewähltem Grund).
    const requiredMissing = () => {
        const m = [];
        if (!veh.make.trim()) m.push("Marke");
        if (!veh.model.trim()) m.push("Modell");
        if (!veh.vehicle_type) m.push("Fahrzeugtyp");
        if (!veh.first_registration_date) m.push("Erstzulassung");
        if (!veh.condition) m.push("Zustand");
        if (veh.mileage === "" || veh.mileage == null) m.push("Laufleistung (km)");
        if (!veh.tuev_until && !(veh.tuev_none && veh.tuev_none_reason)) m.push("TÜV");
        if (!veh.vin.trim()) m.push("Fahrgestellnummer (VIN)");
        return m;
    };
    const vinIncomplete = veh.vin && veh.vin.trim().length > 0 && veh.vin.trim().length < 17;
    // Erstzulassung (Datum) setzen + Jahr daraus ableiten.
    const setRegDate = (v) => setVeh((p) => ({ ...p, first_registration_date: v, year: v ? v.slice(0, 4) : p.year }));
    const navigate = useNavigate();
    const [showAiInfo, setShowAiInfo] = useState(false);
    // Jun-2026 (User-Wunsch · Msg 509) — Hinweis „Preis jetzt neu berechnen", wenn
    // der Nutzer gerade von „Genauere Daten" (KBA-Detailseite) zurückgekehrt ist.
    const [showRecalcHint, setShowRecalcHint] = useState(false);
    useEffect(() => {
        try {
            if (sessionStorage.getItem(KBA_FLAG(listingId)) === "1") {
                setShowRecalcHint(true);
                sessionStorage.removeItem(KBA_FLAG(listingId));
            }
        } catch { /* sessionStorage nicht verfügbar */ }
    }, [listingId]);
    // Jun-2026 (User-Wunsch) — automatisch generierter Hinweistext: welche Daten
    // fließen in die KI-Preisermittlung ein?
    const aiInfoText = () => {
        const p = [];
        if (veh.make) p.push(`Marke „${veh.make}"`);
        if (veh.model) p.push(`Modell „${veh.model}"`);
        if (veh.first_registration_date) p.push(`Erstzulassung ${veh.first_registration_date.split("-").reverse().join(".")}`);
        if (veh.mileage !== "" && veh.mileage != null) p.push(`Laufleistung ${Number(veh.mileage).toLocaleString("de-DE")} km`);
        if (veh.vehicle_type) p.push(`Fahrzeugtyp „${veh.vehicle_type}"`);
        if (veh.vin) p.push(`FIN ${veh.vin}`);
        if (veh.condition) p.push(`Zustand „${veh.condition}"`);
        const base = p.length ? p.join(", ") : "— bitte zuerst die Fahrzeugdaten ausfüllen —";
        return `In die KI-Preisermittlung fließen ein: ${base}. Zusätzlich werden vergleichbare Marktangebote (gleiche Marke/Modell, Baujahr & Laufleistung) ausgewertet`
            + (checklist ? " sowie die Kostenabzüge aus der Ankauf-Checkliste (fehlende Papiere/Schlüssel, GW-Preisliste)" : "")
            + ". Daraus wird ein empfohlener Händler-Ankaufspreis inkl. Verhandlungsrahmen errechnet.";
    };
    // Jun-2026 (User-Wunsch) — „Genauere Daten": zu den vollständigen Fahrzeug-
    // Stammdaten des Entwurf-Fahrzeugs (KBA-/Hersteller-Abfrage, Schäden, Fotos).
    const goToMasterData = () => {
        if (!saved?.draft_vehicle_id) {
            toast.error("Bitte zuerst das Eintausch-Fahrzeug speichern — danach stehen die Fahrzeug-Stammdaten (KBA, Hersteller, Schäden) zur Verfügung.");
            return;
        }
        // Merker setzen, damit nach Rückkehr der „Preis neu berechnen"-Hinweis erscheint.
        try { sessionStorage.setItem(KBA_FLAG(listingId), "1"); } catch { /* noop */ }
        toast.message("Öffne Fahrzeug-Stammdaten des Entwurf-Fahrzeugs …");
        navigate(`/edit-listing/${saved.draft_vehicle_id}`);
    };
    const pdfUrl = saved ? `${process.env.REACT_APP_BACKEND_URL}/api/listings/${listingId}/dispo/tradein/contract.pdf?ts=${Date.now()}` : null;

    // ZB1-Scan-Ergebnis auf das Eintausch-Fahrzeug-Formular übernehmen.
    // Jul-2026 (User-Wunsch · 450) — Feld-Parität zum Inserat: alle relevanten
    // ZB1-Felder übernehmen (ccm/Leistung/HSN/TSN/Kraftstoff/Schadstoff/J-Klasse).
    // D.1 = Marke, Feld 2 = Hersteller, D.2 (type_variant) NIE als Marke.
    const applyScan = (fields) => {
        setVeh((p) => ({
            ...p,
            make: fields.brand || fields.manufacturer || p.make,
            model: fields.model || p.model,
            first_registration_date: fields.first_registration_date ? String(fields.first_registration_date).slice(0, 10) : p.first_registration_date,
            year: fields.first_registration_date ? (yearFromDate(fields.first_registration_date) || p.year) : p.year,
            mileage: fields.mileage != null && fields.mileage !== "" ? fields.mileage : p.mileage,
            displacement_ccm: fields.displacement != null && fields.displacement !== "" ? fields.displacement : p.displacement_ccm,
            power_kw: fields.power_kw != null && fields.power_kw !== "" ? fields.power_kw : p.power_kw,
            fuel: fields.fuel || p.fuel,
            emission_class: fields.emission_class || p.emission_class,
            hsn: fields.hsn || p.hsn,
            tsn: fields.tsn || p.tsn,
            vehicle_class: fields.vehicle_class || p.vehicle_class,
            tuev_until: fields.tuev_until ? String(fields.tuev_until).slice(0, 7) : p.tuev_until,
            vin: fields.vin || p.vin,
        }));
        toast.success("Fahrzeugdaten aus dem Scan übernommen — bitte prüfen.");
        // Fahrzeugtyp per KI aus Marke + Modell + EU-Klasse (J) ableiten.
        classifyType({
            brand: fields.brand || fields.manufacturer,
            model: fields.model,
            vehicle_class: fields.vehicle_class,
        }).catch(() => {});
    };

    // Jul-2026 (User-Wunsch · 451) — Fahrzeugtyp (Bauform) per gemeinsamer
    // KI-Klassifikation (identisch zum Inserat-Scan).
    const classifyType = async ({ brand, model, vehicle_class }) => {
        const res = await classifyVehicleType({ brand, model, vehicle_class });
        const vt = res?.vehicle_type;
        if (vt) {
            setVeh((p) => ({ ...p, vehicle_type: vt }));
            toast.message(`Fahrzeugtyp erkannt: „${vt}"${res?.source === "ai" ? " (KI)" : ""}`);
        }
    };

    // Handy-Scan-Handoff (C2): ZB1-Felder aus dem Handy-Bündel übernehmen;
    // Ausweis/Belege werden serverseitig im Belege-Portal abgelegt.
    const handleMobileScan = ({ documents }) => {
        const zb1 = (documents || []).find((d) => d.kind === "zb1" || d.document_type === "zb1" || d.document_type === "coc");
        const f = zb1?.fields || {};
        if (Object.keys(f).length) {
            applyScan(f);
        } else {
            toast.success("Dokumente vom Handy empfangen (im Belege-Portal abgelegt).");
        }
    };

    const runSearch = async () => {
        const q = searchQ.trim();
        if (q.length < 2) { toast.error("Bitte mind. 2 Zeichen (FIN / Nr. / Marke) eingeben"); return; }
        setSearching(true);
        try {
            const r = await api.get(`/listings/${listingId}/dispo/tradein/search`, { params: { q } });
            setResults(r.data?.items || []);
            if (!(r.data?.items || []).length) toast.message("Keine passenden Fahrzeuge gefunden");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Suche fehlgeschlagen");
        } finally { setSearching(false); }
    };

    const pickResult = (it) => {
        setVeh({
            make: it.brand || "", model: it.model || "",
            category: it.category || "motorcycle",
            vehicle_type: it.vehicle_type || "",
            first_registration_date: it.first_registration_date || "",
            year: it.year || "", mileage: it.mileage ?? "",
            displacement_ccm: it.displacement ?? it.displacement_ccm ?? "",
            power_kw: it.power_kw ?? "",
            vin: it.vin || "",
            condition: CONDITION_OPTIONS.includes(it.condition) ? it.condition : "Gebrauchtfahrzeug",
            tuev_until: it.tuev_until || "",
            notes: `Bereits angelegt: ${it.internal_number || ""}${it.license_plate ? " · " + it.license_plate : ""}`.trim(),
        });
        setLinkedId(it.id);
        setPanel(null);
        toast.success(`Fahrzeug ${it.internal_number || it.brand} übernommen`);
    };

    const submit = async ({ silent = false } = {}) => {
        const missing = requiredMissing();
        if (missing.length) { if (!silent) toast.error(`Pflichtfelder fehlen: ${missing.join(", ")}`); return false; }
        if (!silent) setSaving(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/tradein`, {
                vehicle: { ...veh, year: Number(yearFromDate(veh.first_registration_date)) || Number(veh.year) || null, mileage: Number(veh.mileage) || 0 },
                price: Number(price) || 0,
                ...(checklist ? { checklist } : {}),
                ...(linkedId ? { existing_vehicle_id: linkedId } : {}),
            });
            setSaved(r.data.dispo_tradein);
            if (!silent) {
                toast.success(`Ankaufsvertrag ${r.data.dispo_tradein.contract_number} erstellt · Fahrzeug ${r.data.dispo_tradein.draft_internal_number} angelegt`);
                autosave.markSaved();
                await onSaved?.();
                // Jun-2026 (User-Wunsch) — nach Speichern/Aktualisieren direkt zu „Zahlung & Rabatt".
                if (onGoToPayment) {
                    toast.message("Weiter zu Zahlung & Rabatt …");
                    onGoToPayment();
                }
            }
            return true;
        } catch (e) {
            if (!silent) toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
            return false;
        } finally { if (!silent) setSaving(false); }
    };
    // Auto-Speichern nur, wenn der Ankauf bereits existiert (sonst würde bei jedem
    // Feld-Blur vorzeitig ein Ankaufsvertrag/Entwurf-Fahrzeug erzeugt).
    // Jul-2026 (User-Wunsch · 450) — Beim Anlegen/Aktualisieren des Ankaufsvertrags
    // wird der KI-Ankaufspreis verpflichtend EINMAL neu berechnet und der Nutzer
    // informiert (der vereinbarte Preis wird NICHT automatisch überschrieben).
    const submitWithRecalc = async () => {
        const missing = requiredMissing();
        if (missing.length) { toast.error(`Pflichtfelder fehlen: ${missing.join(", ")}`); return; }
        setSaving(true);
        try {
            try {
                const r = await api.post(`/listings/${listingId}/dispo/tradein/ai-price`, {
                    vehicle: { ...veh, year: Number(yearFromDate(veh.first_registration_date)) || Number(veh.year) || null, mileage: Number(veh.mileage) || 0 },
                    ...(checklist ? { checklist } : {}),
                });
                setAiResult(r.data);
                if (r.data?.suggested_purchase) {
                    toast.success(`KI-Ankaufspreis neu berechnet: Richtwert ${formatPrice(r.data.suggested_purchase)} · vereinbart ${formatPrice(Number(price) || 0)}`);
                } else {
                    toast.message("KI-Preis neu berechnet — keine belastbare Schätzung möglich.");
                }
            } catch { toast.message("KI-Neuberechnung nicht möglich — es wird trotzdem gespeichert."); }
        } finally { setSaving(false); }
        await submit();
    };

    const autosave = useAutosaveOnBlur(
        () => submit({ silent: true }),
        () => JSON.stringify({ veh, price }),
        { enabled: Boolean(saved) },
    );

    // Jun-2026 (User-Wunsch) — KI-Preisermittlung für das Eintausch-Fahrzeug.
    // Schätzt einen Ankaufspreis (Händlereinkauf) anhand der Fahrzeugdaten.
    const runAiPrice = async () => {
        const missing = requiredMissing();
        if (missing.length) { toast.error(`Bitte zuerst alle Pflichtfelder ausfüllen: ${missing.join(", ")}`); return; }
        setAiLoading(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/tradein/ai-price`, {
                vehicle: { ...veh, year: Number(yearFromDate(veh.first_registration_date)) || Number(veh.year) || null, mileage: Number(veh.mileage) || 0 },
                ...(checklist ? { checklist } : {}),
            });
            setAiResult(r.data);
            if (r.data?.suggested_purchase) {
                setPrice(r.data.suggested_purchase);
                toast.success(`KI-Ankaufspreis übernommen: ${formatPrice(r.data.suggested_purchase)}`);
            } else {
                toast.message(r.data?.rationale || "Keine KI-Schätzung möglich");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Preisermittlung fehlgeschlagen");
        } finally { setAiLoading(false); }
    };

    // Jun-2026 (User-Wunsch) — DAT-Preisermittlung (MOCK bis API-Keys vorliegen).
    const runDatPrice = async () => {
        const missing = requiredMissing();
        if (missing.length) { toast.error(`Bitte zuerst alle Pflichtfelder ausfüllen: ${missing.join(", ")}`); return; }
        setDatLoading(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/tradein/dat-price`, {
                vehicle: { ...veh, year: Number(yearFromDate(veh.first_registration_date)) || Number(veh.year) || null, mileage: Number(veh.mileage) || 0 },
                ...(checklist ? { checklist } : {}),
            });
            setAiResult(r.data);
            if (r.data?.suggested_purchase) {
                setPrice(r.data.suggested_purchase);
                toast.success(`DAT-Ankaufspreis übernommen: ${formatPrice(r.data.suggested_purchase)}`);
            } else {
                toast.message(r.data?.rationale || "Keine DAT-Schätzung möglich");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "DAT-Preisermittlung fehlgeschlagen");
        } finally { setDatLoading(false); }
    };

    // Jun-2026 (User-Wunsch) — Ankauf-Freigabe beim Filialleiter/Verkaufsleiter einholen.
    // Die erste Anforderung geht IMMER automatisch raus (nach dem Speichern); der
    // Button dient danach nur noch als Erinnerung.
    const autoReqRef = useRef(false);
    const requestApproval = async ({ auto = false } = {}) => {
        setReqApproval(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/tradein/request-approval`);
            setApproval(r.data?.approval || { status: "pending" });
            toast.success(auto
                ? "Ankauf-Freigabe automatisch angefordert — Filialleiter wurde informiert"
                : "Erinnerung gesendet — Filialleiter wurde erneut informiert");
        } catch (e) {
            if (!auto) toast.error(e.response?.data?.detail || "Freigabe-Anfrage fehlgeschlagen");
        } finally { setReqApproval(false); }
    };

    // Erste Freigabe-Aufforderung automatisch auslösen, sobald gespeichert.
    useEffect(() => {
        if (saved && !approval && !autoReqRef.current) {
            autoReqRef.current = true;
            requestApproval({ auto: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saved, approval]);

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="dispo-modal-tradein" onBlur={autosave.onBlur}
                onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
                <FeatureGuideTriangle guideKey="tradein" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-orange-600" /> Eintausch-Fahrzeug (Inzahlungnahme)<span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-6">Schritt 3 von 6</span></DialogTitle><DialogDescription>Fahrzeugdaten erfassen (Format wie im Inserat), Ankaufspreis ermitteln und Ankaufsvertrag erzeugen.</DialogDescription></DialogHeader>
                {saved && <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />}

                {/* Jun-2026 (User-Wunsch · Msg 509) — nach Rückkehr von „Genauere Daten":
                    Erinnerung, den Ankaufspreis mit den genaueren Fahrzeugdaten neu zu ermitteln. */}
                {showRecalcHint && (
                    <div className="bg-violet-50 border-2 border-violet-300 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="dispo-tradein-recalc-hint">
                        <Sparkles className="w-4 h-4 text-violet-600 shrink-0" />
                        <span className="text-xs font-bold text-violet-900">Genauere Fahrzeugdaten erfasst</span>
                        <span className="text-xs text-violet-700">— jetzt den Ankaufspreis neu berechnen.</span>
                        <Button type="button" size="sm" onClick={() => { setShowRecalcHint(false); runAiPrice(); }} disabled={aiLoading}
                            className="ml-auto bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs" data-testid="dispo-tradein-recalc-btn">
                            {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Calculator className="w-3.5 h-3.5 mr-1" />}
                            Preis jetzt neu berechnen
                        </Button>
                        <button type="button" onClick={() => setShowRecalcHint(false)} aria-label="Hinweis ausblenden"
                            className="text-violet-400 hover:text-violet-700" data-testid="dispo-tradein-recalc-dismiss">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {saved && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-center gap-2" data-testid="dispo-tradein-saved">
                        <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
                        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-emerald-800">Ankaufsvertrag {saved.contract_number}</span>
                            <span className="text-xs text-emerald-700">· Fahrzeug {saved.draft_internal_number} als Entwurf angelegt</span>
                        </div>
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 ml-auto" data-testid="dispo-tradein-pdf-link">
                            <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-2"><Printer className="w-3.5 h-3.5 mr-1" /> Ankaufsvertrag (PDF)</Button>
                        </a>
                    </div>
                )}

                {/* Jul-2026 (User-Wunsch) — ZB1 auch nach dem Speichern erneut scannen. */}
                {saved && (
                    <Button type="button" size="sm" variant="outline" onClick={() => setPanel(panel === "scan" ? null : "scan")} className={`h-8 text-xs border-2 ${panel === "scan" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-300 text-emerald-700"}`} data-testid="dispo-tradein-rescan-btn">
                        <ScanLine className="w-3.5 h-3.5 mr-1" /> ZB1 erneut scannen
                    </Button>
                )}

                {/* Jun-2026 (User-Wunsch) — Modus-Umschalter: ZB1 scannen / bereits angelegtes Fahrzeug suchen */}
                {!saved && (
                    <div className="flex gap-2" data-testid="dispo-tradein-mode-switch">
                        <Button type="button" size="sm" variant={panel === "scan" ? "default" : "outline"} onClick={() => setPanel(panel === "scan" ? null : "scan")} className={`h-8 text-xs border-2 ${panel === "scan" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`} data-testid="dispo-tradein-mode-scan">
                            <ScanLine className="w-3.5 h-3.5 mr-1" /> ZB1 scannen
                        </Button>
                        <Button type="button" size="sm" variant={panel === "search" ? "default" : "outline"} onClick={() => setPanel(panel === "search" ? null : "search")} className={`h-8 text-xs border-2 ${panel === "search" ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}`} data-testid="dispo-tradein-mode-search">
                            <Search className="w-3.5 h-3.5 mr-1" /> Bereits angelegt
                        </Button>
                    </div>
                )}

                {/* Jul-2026 (User-Wunsch) — Beschreibungstext entfernt; QR direkt in den
                    Scan-Block integriert (qrSlot), wie beim Inserat-Scan. */}
                {panel === "scan" && (
                    <div data-testid="dispo-tradein-scan-panel">
                        <ZB1ScanCard
                            onResult={applyScan}
                            qrSlot={<MobileZb1QrCard variant="inline" purpose="papers" listingId={listingId} onScanned={handleMobileScan} />}
                        />
                    </div>
                )}

                {panel === "search" && !saved && (
                    <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 space-y-2" data-testid="dispo-tradein-search-panel">
                        <Label className="text-xs font-bold">Bereits angelegtes Fahrzeug suchen (FIN / interne Nr. / Marke / Kennzeichen)</Label>
                        <div className="flex gap-2">
                            <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }} placeholder="z. B. WBA… oder F-2026-0042" className="h-9" data-testid="dispo-tradein-search-input" />
                            <Button type="button" onClick={runSearch} disabled={searching} className="h-9 bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-tradein-search-btn">
                                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suchen"}
                            </Button>
                        </div>
                        {results.length > 0 && (
                            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="dispo-tradein-search-results">
                                {results.map((it) => (
                                    <button key={it.id} type="button" onClick={() => pickResult(it)} className="w-full text-left bg-white border border-gray-200 hover:border-orange-400 rounded-sm px-3 py-2 flex items-center gap-2" data-testid={`dispo-tradein-result-${it.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold truncate">{it.brand} {it.model} {it.year ? `· ${it.year}` : ""}</div>
                                            <div className="text-[11px] text-gray-500 font-mono-num truncate">{it.internal_number}{it.vin ? ` · FIN ${it.vin}` : ""}{it.license_plate ? ` · ${it.license_plate}` : ""}</div>
                                        </div>
                                        {it.sold && <span className="text-[10px] font-bold text-red-600 shrink-0">VERKAUFT</span>}
                                        <Check className="w-4 h-4 text-orange-500 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {linkedId && (
                    <div className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="dispo-tradein-linked-hint">
                        <Check className="w-3.5 h-3.5" /> Verknüpft mit bereits angelegtem Fahrzeug
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Marke *</Label>
                        <BrandCombobox
                            value={veh.make}
                            onValueChange={(v) => upd("make", v)}
                            placeholder="Marke wählen…"
                            searchPlaceholder="Marke suchen oder eingeben…"
                            testId="dispo-tradein-make"
                            triggerClassName="mt-1 h-10"
                            groups={[{ items: MOTO_BRANDS.map((b) => ({ value: b })) }]}
                        />
                    </div>
                    <div><Label className="text-xs">Modell *</Label><Input value={veh.model} onChange={(e) => upd("model", e.target.value)} className="mt-1" data-testid="dispo-tradein-model" /></div>
                    {/* Jun-2026 (User-Wunsch) — Fahrzeugtyp · Zustand · TÜV bis in einer Zeile */}
                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.3fr] gap-3">
                        <div><Label className="text-xs">Fahrzeugtyp *</Label>
                            <select value={veh.vehicle_type} onChange={(e) => upd("vehicle_type", e.target.value)} className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white" data-testid="dispo-tradein-vehicle-type">
                                <option value="">– bitte wählen –</option>
                                {MOTORCYCLE_VEHICLE_TYPES.map((vt) => <option key={vt} value={vt}>{vt}</option>)}
                            </select>
                        </div>
                        <div><Label className="text-xs">Zustand *</Label>
                            <select value={veh.condition} onChange={(e) => upd("condition", e.target.value)} className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white" data-testid="dispo-tradein-condition">
                                {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div><Label className="text-xs">TÜV bis (Monat / Jahr) *</Label>
                            <div className="mt-1"><MonthYearPicker value={veh.tuev_until || ""} onChange={(v) => setVeh((p) => ({ ...p, tuev_until: v, tuev_none: v ? false : p.tuev_none, tuev_none_reason: v ? "" : p.tuev_none_reason }))} yearsBack={2} yearsAhead={8} testId="dispo-tradein-tuev" disabled={veh.tuev_none} /></div>
                            <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-gray-600 cursor-pointer">
                                <input type="checkbox" checked={veh.tuev_none} onChange={(e) => setVeh((p) => ({ ...p, tuev_none: e.target.checked, tuev_until: e.target.checked ? "" : p.tuev_until, tuev_none_reason: e.target.checked ? p.tuev_none_reason : "" }))} className="w-3.5 h-3.5 accent-orange-500" data-testid="dispo-tradein-tuev-none" />
                                Kein TÜV
                            </label>
                            {veh.tuev_none && (
                                <div className="mt-1 space-y-1 pl-1" data-testid="dispo-tradein-tuev-none-reason">
                                    <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                                        <input type="radio" name="tuev-none-reason" checked={veh.tuev_none_reason === "expired"} onChange={() => upd("tuev_none_reason", "expired")} className="accent-orange-500" data-testid="dispo-tradein-tuev-expired" /> Abgelaufen
                                    </label>
                                    <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                                        <input type="radio" name="tuev-none-reason" checked={veh.tuev_none_reason === "under_50ccm"} onChange={() => upd("tuev_none_reason", "under_50ccm")} className="accent-orange-500" data-testid="dispo-tradein-tuev-under50" /> Kein TÜV (unter 50 ccm)
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                    <div><Label className="text-xs">Erstzulassung (B) *</Label>
                        <Input type="date" max={new Date().toISOString().slice(0, 10)} value={veh.first_registration_date} onChange={(e) => setRegDate(e.target.value)} className="mt-1 font-mono-num" data-testid="dispo-tradein-erstzulassung" />
                    </div>
                    <div><Label className="text-xs">Laufleistung (km) *</Label><Input type="number" min="0" value={veh.mileage} onChange={(e) => upd("mileage", e.target.value)} className="mt-1 font-mono-num" data-testid="dispo-tradein-mileage" /></div>
                    <div><Label className="text-xs">Hubraum (ccm)</Label><Input type="number" min="0" value={veh.displacement_ccm} onChange={(e) => upd("displacement_ccm", e.target.value)} placeholder="z. B. 125" className="mt-1 font-mono-num" data-testid="dispo-tradein-ccm" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">Fahrgestellnummer (VIN) *</Label>
                        <Input value={veh.vin} maxLength={17} onChange={(e) => upd("vin", e.target.value.toUpperCase())} placeholder="z. B. WB10…" className="mt-1 font-mono-num uppercase" data-testid="dispo-tradein-vin"
                            autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} name="tw-tradein-vin" data-lpignore="true" data-1p-ignore data-form-type="other" />
                        {vinIncomplete && (
                            <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1" data-testid="dispo-tradein-vin-warning">
                                <Info className="w-3 h-3" /> Die Fahrgestellnummer hat {veh.vin.trim().length} von 17 Stellen — bitte vollständig eingeben.
                            </div>
                        )}
                    </div>
                    <div className="sm:col-span-2"><Label className="text-xs">Bemerkungen (Zustand, Mängel …)</Label><Textarea value={veh.notes} onChange={(e) => upd("notes", e.target.value)} className="mt-1" data-testid="dispo-tradein-notes" autoComplete="off" name="tw-tradein-notes" data-lpignore="true" data-1p-ignore data-form-type="other" /></div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-sm px-3 py-2 mt-1 space-y-2" data-testid="dispo-tradein-price-block">
                    <div className="flex items-center justify-between gap-2">
                        <Label className="text-sm font-bold">Vereinbarter Ankaufspreis (Inzahlungnahme)</Label>
                        <div className="flex items-center gap-3">
                            {customerWishPrice != null && Number(customerWishPrice) > 0 && (
                                <div className="text-right leading-tight" data-testid="dispo-tradein-wish-price">
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Wunschpreis Kunde</div>
                                    <div className="text-sm font-bold text-gray-700 font-mono-num">{(Number(customerWishPrice)).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                            )}
                            <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="w-32 h-9 text-right font-bold" data-testid="dispo-tradein-price" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="inline-flex items-center gap-1">
                            <Button type="button" size="sm" variant="outline" onClick={runAiPrice} disabled={aiLoading}
                                className="h-8 text-xs border-2 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="dispo-tradein-ai-price-btn">
                                {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                                KI-Preisermittlung
                            </Button>
                            <button type="button" onClick={() => setShowAiInfo((s) => !s)} title="Welche Daten werden zur Preisermittlung genutzt?" aria-label="Info zur KI-Preisermittlung"
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-violet-300 text-violet-600 hover:bg-violet-50" data-testid="dispo-tradein-ai-info-btn">
                                <Info className="w-3 h-3" />
                            </button>
                        </div>
                        <Button type="button" size="sm" variant="outline" onClick={goToMasterData}
                            className="h-8 text-xs border-2 border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="dispo-tradein-master-data-btn">
                            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Genauere Daten
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={runDatPrice} disabled={datLoading}
                            className="h-8 text-xs border-2 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="dispo-tradein-dat-price-btn">
                            {datLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                            Preis mit DAT ermitteln
                        </Button>
                        {aiResult?.suggested_purchase != null && (
                            <div className="flex flex-col gap-0.5 text-[11px]" data-testid="dispo-tradein-ai-range">
                                <span className="text-violet-800 font-bold font-mono-num">
                                    Ankauf-Rahmen (Händler-Einkauf) {formatPrice(aiResult.purchase_range_min ?? aiResult.suggested_purchase)}–{formatPrice(aiResult.purchase_range_max ?? aiResult.suggested_purchase)}
                                </span>
                                {aiResult.suggested_retail ? (
                                    <span className="text-gray-700 font-mono-num">
                                        VK-Richtwert (Verkauf) {formatPrice(aiResult.suggested_retail)}
                                        {aiResult.range_min ? ` · VK-Rahmen ${formatPrice(aiResult.range_min)}–${formatPrice(aiResult.range_max || 0)}` : ""}
                                    </span>
                                ) : null}
                            </div>
                        )}
                    </div>
                    {showAiInfo && (
                        <div className="text-[11px] text-violet-900 bg-violet-50 border border-violet-200 rounded-sm px-2.5 py-2 flex items-start gap-1.5" data-testid="dispo-tradein-ai-info">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-violet-500" />
                            <span>{aiInfoText()}</span>
                        </div>
                    )}
                    {aiResult?.rationale && (
                        <div className="text-[11px] text-gray-800 bg-white border border-violet-200 rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="dispo-tradein-ai-rationale">
                            <Sparkles className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                            <span>{aiResult.rationale}{aiResult.mocked ? " (Heuristik — KI nicht erreichbar)" : ""}{typeof aiResult.comparables_count === "number" ? ` · ${aiResult.comparables_count} Vergleichsfahrzeug(e)` : ""}</span>
                        </div>
                    )}
                    {aiResult?.deductions_total > 0 && (
                        <div className="text-[11px] rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5 space-y-0.5" data-testid="dispo-tradein-deductions">
                            <div className="flex items-center justify-between font-bold text-amber-800">
                                <span>Abzüge Ankauf-Checkliste (GW-Preisliste)</span>
                                <span className="font-mono-num">− {formatPrice(aiResult.deductions_total)}</span>
                            </div>
                            {(aiResult.deductions || []).map((d) => (
                                <div key={d.sku} className="flex items-center justify-between text-amber-700">
                                    <span>{d.label} <span className="opacity-60">({d.sku})</span></span>
                                    <span className="font-mono-num">{formatPrice(d.gross)}</span>
                                </div>
                            ))}
                            {aiResult.suggested_purchase_before_deductions != null && (
                                <div className="flex items-center justify-between text-amber-900 font-semibold pt-0.5 border-t border-amber-200">
                                    <span>Vor Abzügen</span>
                                    <span className="font-mono-num">{formatPrice(aiResult.suggested_purchase_before_deductions)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Genehmigung zum Ankauf einholen (Filialleiter/Verkaufsleiter) — Haken
                    ist fix, die erste Freigabe-Aufforderung geht automatisch raus. */}
                <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5 space-y-2" data-testid="dispo-tradein-approval-block">
                    <label className="flex items-center gap-2 text-sm font-bold text-amber-900 cursor-not-allowed" title="Verpflichtend — nicht abwählbar">
                        <input type="checkbox" checked disabled readOnly className="w-4 h-4 accent-amber-600" data-testid="dispo-tradein-await-approval" />
                        Auf Genehmigung und Freigabe warten
                    </label>
                    {approval?.status === "approved" ? (
                        <div className="text-[12px] text-emerald-700 flex items-center gap-1.5" data-testid="dispo-tradein-approval-approved"><Check className="w-3.5 h-3.5" /> Ankauf freigegeben.</div>
                    ) : approval?.status === "pending" ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] text-amber-800 inline-flex items-center gap-1.5" data-testid="dispo-tradein-approval-pending">
                                <Clock className="w-3.5 h-3.5" /> Freigabe angefordert — wartet auf Filialleiter/Verkaufsleiter.
                            </span>
                            <Button type="button" size="sm" variant="outline" onClick={() => requestApproval()} disabled={reqApproval}
                                className="h-8 text-xs border-2 border-amber-400 text-amber-800 hover:bg-amber-100 ml-auto" data-testid="dispo-tradein-request-approval">
                                {reqApproval ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Bell className="w-3.5 h-3.5 mr-1" />}
                                Erinnerung senden
                            </Button>
                        </div>
                    ) : (
                        <div className="text-[12px] text-amber-900 font-semibold" data-testid="dispo-tradein-approval-auto">
                            {saved ? "Freigabe wird angefordert …" : "Die Freigabe wird nach dem Speichern automatisch angefordert."}
                        </div>
                    )}
                    {!saved && <div className="text-[11px] text-amber-900 font-semibold">Bitte zuerst das Eintausch-Fahrzeug speichern — die Freigabe wird dann automatisch angefordert.</div>}
                </div>

                <DialogFooter>
                    <DispoBackButton onBack={onBack} />
                    <Button variant="outline" onClick={onClose}>Schließen</Button>
                    <Button onClick={submitWithRecalc} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-tradein-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (saved ? "Aktualisieren & Vertrag neu erzeugen" : "Fahrzeug anlegen & Ankaufsvertrag")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
